import { alpha, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { MEDIA_FRAME_ASPECT_RATIOS, type MediaFrame } from '@provision/landing-page-ui';
import { type LandingPageImageFit } from '@provision/landing-page-utils';
import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  cropGeometry,
  PREVIEW_HEIGHT,
  type Size,
} from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/crop-geometry';
import {
  blockFieldName,
  IMAGE_FIT_OPTIONS,
  type LandingPageFormValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { isValidHttpUrl } from 'src/utils/url';

const KEY_STEP = 5;

const clamp = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

interface ImageFrameEditorProps {
  index: number;
  prefix: MediaFrame;
  url: string;
  readOnly: boolean;
}

export const ImageFrameEditor = ({
  index,
  prefix,
  url,
  readOnly,
}: ImageFrameEditorProps): ReactElement | null => {
  const { control, setValue } = useFormContext<LandingPageFormValues>();
  const fitName = blockFieldName(index, `${prefix}ImageFit`);
  const focusXName = blockFieldName(index, `${prefix}ImageFocusX`);
  const focusYName = blockFieldName(index, `${prefix}ImageFocusY`);
  const fit = useWatch({ control, name: fitName });
  const focusX = useWatch({ control, name: focusXName });
  const focusY = useWatch({ control, name: focusYName });

  const dragRef = useRef<{ start: number; startFocus: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(0);
  // Keyed by URL so a new URL starts clean. A reset effect would race a cached image, whose
  // load can land before the effect runs and then get wiped.
  const [loaded, setLoaded] = useState<{ url: string; size: Size } | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const natural = loaded?.url === url ? loaded.size : null;
  const hasError = failedUrl === url;

  const readSize = useCallback(
    (img: HTMLImageElement | null): void => {
      if (img?.complete && img.naturalWidth) {
        setLoaded({ url, size: { width: img.naturalWidth, height: img.naturalHeight } });
      }
    },
    [url],
  );

  // Measured on attach: the block accordion mounts this mid-transition, so waiting on a later
  // resize can leave the width at 0.
  const measureContainer = useCallback((node: HTMLDivElement | null) => {
    if (!node) return undefined;
    setPreviewWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => setPreviewWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!url || !isValidHttpUrl(url) || hasError) return null;

  const geometry =
    natural && previewWidth
      ? cropGeometry(natural, previewWidth, MEDIA_FRAME_ASPECT_RATIOS[prefix])
      : null;
  const cropping = fit === 'cover' && geometry !== null && geometry.slack > 0;
  const focus = geometry?.axis === 'x' ? focusX : focusY;
  const focusName = geometry?.axis === 'x' ? focusXName : focusYName;

  const grabCursor = dragging ? 'grabbing' : 'grab';

  const setFocus = (next: number): void => {
    setValue(focusName, clamp(next), { shouldDirty: true });
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (readOnly || !geometry) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      start: geometry.axis === 'x' ? event.clientX : event.clientY,
      startFocus: focus,
    };
    setDragging(true);
  };

  const endDrag = (): void => {
    dragRef.current = null;
    setDragging(false);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag || !geometry) return;
    const delta = (geometry.axis === 'x' ? event.clientX : event.clientY) - drag.start;
    setFocus(drag.startFocus + (delta / geometry.slack) * 100);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (readOnly || !geometry) return;
    const back = geometry.axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
    const forward = geometry.axis === 'x' ? 'ArrowRight' : 'ArrowDown';
    if (event.key !== back && event.key !== forward) return;
    event.preventDefault();
    setFocus(focus + (event.key === forward ? KEY_STEP : -KEY_STEP));
  };

  return (
    <Box>
      <Box
        ref={measureContainer}
        sx={{
          position: 'relative',
          height: PREVIEW_HEIGHT,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            ...(geometry ? geometry.image : { width: '100%', height: '100%' }),
          }}
        >
          <Box
            component="img"
            src={url}
            alt="Image preview"
            ref={readSize}
            onLoad={(event) => readSize(event.currentTarget)}
            onError={() => setFailedUrl(url)}
            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
          />
          {cropping ? (
            <Box
              role="slider"
              tabIndex={readOnly ? -1 : 0}
              aria-label="Image focus"
              aria-orientation={geometry.axis === 'x' ? 'horizontal' : 'vertical'}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={focus}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onLostPointerCapture={endDrag}
              onKeyDown={onKeyDown}
              sx={{
                position: 'absolute',
                ...geometry.crop,
                left: geometry.axis === 'x' ? (geometry.slack * focus) / 100 : 0,
                top: geometry.axis === 'y' ? (geometry.slack * focus) / 100 : 0,
                outline: '2px solid',
                outlineColor: 'common.white',
                outlineOffset: -2,
                // Dims everything outside the crop; the wrapper's overflow clips it.
                boxShadow: (theme) =>
                  `0 0 0 9999px ${alpha(theme.palette.common.black, 0.55)}`,
                cursor: readOnly ? 'default' : grabCursor,
                touchAction: 'none',
                '&:focus-visible': { outlineColor: 'primary.main' },
              }}
            />
          ) : null}
        </Box>

        <ToggleButtonGroup
          exclusive
          size="small"
          disabled={readOnly}
          value={fit}
          onChange={(_, next: LandingPageImageFit | null) => {
            if (next) setValue(fitName, next, { shouldDirty: true });
          }}
          aria-label="Image fit"
          sx={{
            position: 'absolute',
            right: (theme) => theme.spacing(1),
            bottom: (theme) => theme.spacing(1),
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.6),
            backdropFilter: 'blur(4px)',
            '& .MuiToggleButton-root': {
              color: 'common.white',
              border: 0,
              px: 1,
              py: 0.25,
              typography: 'caption',
              textTransform: 'none',
            },
            '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover':
              {
                color: 'common.black',
                bgcolor: 'common.white',
              },
          }}
        >
          {IMAGE_FIT_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {cropping && !readOnly ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.75, display: 'block' }}
        >
          Drag the frame to choose what shows.
        </Typography>
      ) : null}
    </Box>
  );
};
